/**
 * ============================================================================
 * ESCROW STORE - ZUSTAND
 * ============================================================================
 * 
 * Global escrow state management
 * 
 * @typedef {Object} Escrow
 * @property {string} id
 */

import { create } from 'zustand';
import { validateId, validateArray, validateObject } from './utils/validators';
import { addItemOptimized, updateItemInArray, limitArraySize, deduplicateById } from './utils/performance';
import { createSelector } from './utils/selectors';

const useEscrowStore = create((set, get) => ({
  // State
  escrows: [],
  currentEscrow: null,
  escrowHistory: [],
  loading: false,
  error: null,
  filters: {
    status: null,
    transactionId: null,
    dateRange: null,
  },
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },

      // Actions
      /**
       * Set escrows with validation and size limit
       * @param {Escrow[]} escrows - Escrows array
       */
      setEscrows: (escrows) => {
        validateArray(escrows, 'Escrows');
        const validated = escrows.map((e) => {
          validateObject(e, 'Escrow');
          if (e.id) validateId(e.id, 'Escrow ID');
          return e;
        });
        const deduplicated = deduplicateById(validated);
        const limited = limitArraySize(deduplicated, 1000);
        set({ escrows: limited });
      },

      /**
       * Add escrow with validation and deduplication
       * @param {Escrow} escrow - Escrow object
       */
      addEscrow: (escrow) => {
        validateObject(escrow, 'Escrow');
        if (!escrow.id) {
          throw new Error('Escrow must have an id');
        }
        set((state) => ({
          escrows: addItemOptimized(state.escrows, escrow, {
            idKey: 'id',
            maxSize: 1000,
            prepend: true,
          }),
        }));
      },

      /**
       * Update escrow with validation
       * @param {string} escrowId - Escrow ID
       * @param {Object} updates - Updates to apply
       */
      updateEscrow: (escrowId, updates) => {
        validateId(escrowId, 'Escrow ID');
        validateObject(updates, 'Updates');
        set((state) => ({
          escrows: updateItemInArray(state.escrows, escrowId, updates),
          currentEscrow:
            state.currentEscrow?.id === escrowId
              ? { ...state.currentEscrow, ...updates }
              : state.currentEscrow,
        }));
      },

  removeEscrow: (escrowId) => {
    set((state) => ({
      escrows: state.escrows.filter((e) => e.id !== escrowId),
      currentEscrow:
        state.currentEscrow?.id === escrowId
          ? null
          : state.currentEscrow,
    }));
  },

  setCurrentEscrow: (escrow) => {
    set({ currentEscrow: escrow });
  },

  setEscrowHistory: (history) => {
    set({ escrowHistory: history });
  },

  addHistoryEntry: (entry) => {
    set((state) => ({
      escrowHistory: [entry, ...state.escrowHistory],
    }));
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 1 },
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        status: null,
        transactionId: null,
        dateRange: null,
      },
      pagination: { ...get().pagination, page: 1 },
    });
  },

  setPagination: (pagination) => {
    set((state) => ({
      pagination: { ...state.pagination, ...pagination },
    }));
  },

  setLoading: (loading) => {
    set({ loading });
  },

  setError: (error) => {
    set({ error });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      escrows: [],
      currentEscrow: null,
      escrowHistory: [],
      loading: false,
      error: null,
      filters: {
        status: null,
        transactionId: null,
        dateRange: null,
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
      },
    });
  },
}));

// Memoized Selectors
export const selectEscrowsByStatus = createSelector(
  (state, status) => {
    return state.escrows.filter((e) => e.status === status);
  }
);

export const selectEscrowStats = createSelector(
  (state) => {
    const { escrows } = state;
    return {
      total: escrows.length,
      byStatus: escrows.reduce((acc, e) => {
        acc[e.status] = (acc[e.status] || 0) + 1;
        return acc;
      }, {}),
      totalAmount: escrows.reduce((sum, e) => sum + (e.amount || 0), 0),
    };
  }
);

export default useEscrowStore;
